import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

const STEP_LABELS = {
  placed:     'Placed',
  processing: 'Processing',
  shipped:    'Shipped',
  delivered:  'Delivered',
};

function fmtDate(d, withTime = false) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  } catch { return String(d); }
}

/** Horizontal step progress for a normal (non-cancelled) shipment. */
function ProgressTimeline({ steps, current, status }) {
  const idx = steps.indexOf(current);
  const halted = status === 'cancelled' || status === 'refunded';
  return (
    <div className="flex items-center w-full">
      {steps.map((s, i) => {
        const done = !halted && i <= idx;
        const active = !halted && i === idx;
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={
                  'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ' +
                  (done ? 'bg-tea-500 text-white' : 'bg-gray-100 text-gray-400') +
                  (active ? ' ring-4 ring-tea-100' : '')
                }
              >
                {done && !active ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (i + 1)}
              </div>
              <span className={'mt-2 text-[11px] font-semibold ' + (done ? 'text-tea-700' : 'text-gray-400')}>
                {STEP_LABELS[s] || s}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={'h-1 flex-1 mx-1 rounded-full ' + (!halted && i < idx ? 'bg-tea-500' : 'bg-gray-100')} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function TrackOrderPage() {
  const [mode, setMode] = useState('order'); // 'order' | 'tracking'
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const payload = mode === 'order'
        ? { orderNumber: orderNumber.trim(), email: email.trim() }
        : { trackingNumber: trackingNumber.trim() };
      const { data } = await api.post('/orders/track', payload);
      setResult(data.order);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const halted = result && (result.status === 'cancelled' || result.status === 'refunded');

  return (
    <div className="min-h-screen bg-white flex flex-col dark:bg-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-200 text-sm font-semibold uppercase tracking-widest mb-3">Order Tracking</p>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">Track your order</h1>
          <p className="mt-4 text-tea-100 text-lg max-w-xl">
            Enter your order number and email, or the tracking number from your shipping confirmation.
          </p>
        </div>
      </section>

      <main id="main-content" tabIndex={-1} className="flex-1 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Lookup card */}
          <div className="card">
            {/* Mode toggle */}
            <div className="flex bg-gray-100 dark:bg-slate-800 rounded-pill p-1 mb-6 w-full max-w-sm mx-auto">
              {[
                { id: 'order', label: 'Order # + Email' },
                { id: 'tracking', label: 'Tracking #' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setMode(t.id); setError(''); }}
                  className={
                    'flex-1 py-2 text-sm font-semibold rounded-pill transition-colors ' +
                    (mode === t.id ? 'bg-white text-tea-700 shadow-sm dark:bg-slate-700 dark:text-tea-300' : 'text-gray-500 dark:text-slate-400')
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4">
              {mode === 'order' ? (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">Order number</label>
                    <input
                      className="input-field font-mono tracking-wider uppercase"
                      placeholder="e.g. 3F9A1C2B"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      maxLength={40}
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">The 8-character code from your order confirmation email.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">Email on the order</label>
                    <input
                      type="email"
                      className="input-field"
                      placeholder="you@lab.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">Tracking number</label>
                  <input
                    className="input-field font-mono tracking-wider"
                    placeholder="e.g. 9400 1000 0000 0000 0000 00"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    maxLength={100}
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">From your shipping confirmation email.</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-4 py-3 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
                  {error}
                </div>
              )}

              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Searching…' : 'Track order'}
              </button>
            </form>
          </div>

          {/* Result */}
          {result && (
            <div className="card mt-6 animate-[fadeIn_0.3s_ease]">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Order</div>
                  <div className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 font-mono">#{result.orderNumber}</div>
                  <div className="text-sm text-gray-500 mt-0.5">Placed {fmtDate(result.placedAt)}</div>
                </div>
                <span
                  className={
                    'inline-flex px-3 py-1.5 rounded-full text-sm font-semibold ' +
                    (halted
                      ? 'bg-red-100 text-red-700'
                      : result.step === 'delivered'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-tea-100 text-tea-700')
                  }
                >
                  {result.statusLabel}
                </span>
              </div>

              {!halted && (
                <div className="mb-8 px-1">
                  <ProgressTimeline steps={result.steps} current={result.step} status={result.status} />
                </div>
              )}

              {/* Shipment details */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {result.destination && (
                  <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-4">
                    <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-1">Shipping to</div>
                    <div className="font-semibold text-gray-800 dark:text-slate-100">
                      {result.recipientFirstName ? `${result.recipientFirstName} · ` : ''}{result.destination}
                    </div>
                  </div>
                )}
                {result.estimatedDelivery && (
                  <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-4">
                    <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-1">Estimated delivery</div>
                    <div className="font-semibold text-gray-800 dark:text-slate-100">{fmtDate(result.estimatedDelivery)}</div>
                  </div>
                )}
              </div>

              {/* Carrier / tracking number */}
              {result.trackingNumber ? (
                <div className="bg-tea-50 border border-tea-200 rounded-2xl p-5 dark:bg-tea-900/20 dark:border-tea-800">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-tea-700 font-semibold mb-1">
                        Tracking number{result.carrier ? ` · ${result.carrier}` : ''}
                      </div>
                      <div className="font-mono font-bold text-gray-900 dark:text-slate-100 tracking-wider break-all">{result.trackingNumber}</div>
                    </div>
                    {result.trackingUrl && (
                      <a href={result.trackingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm px-5 py-2.5">
                        Track with carrier →
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-5 text-sm text-gray-500 dark:text-slate-400">
                  No tracking number yet — you'll get an email with tracking the moment your order ships.
                </div>
              )}

              {/* Carrier scan events */}
              {result.events?.length > 0 && (
                <div className="mt-6">
                  <div className="text-sm font-bold text-gray-800 dark:text-slate-100 mb-3">Tracking history</div>
                  <ol className="relative border-l-2 border-gray-100 dark:border-slate-700 ml-2">
                    {result.events.map((ev, i) => (
                      <li key={i} className="ml-5 pb-5 last:pb-0">
                        <span className={'absolute -left-[7px] w-3 h-3 rounded-full ' + (i === 0 ? 'bg-tea-500' : 'bg-gray-300 dark:bg-slate-600')} />
                        <div className="text-sm font-semibold text-gray-800 dark:text-slate-100">{ev.description || ev.status}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {[ev.location, fmtDate(ev.timestamp, true)].filter(Boolean).join(' · ')}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* Help footer */}
          <div className="text-center mt-8 text-sm text-gray-500 dark:text-slate-400">
            Can't find your order?{' '}
            <Link to="/contact" className="text-tea-600 font-semibold hover:underline">Contact our team</Link>
            {' '}or{' '}
            <Link to="/support" className="text-tea-600 font-semibold hover:underline">read the FAQ</Link>.
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
